/// <amd-module name="Wit/QueriesHub/QueryList/Components/FavoriteQueriesTab" />

import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as Utils from "VSS/Core/Util/String";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { cellFromEvent } from "VSSUI/List";
import { ContextMenuBehavior } from "VSSUI/Menu";
import { IObservableExpression, Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData } from "VSSUI/ZeroData";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { ExtendedHierarchyItem, ObservableFavoriteItemsGroup, QueryItem } from "Wit/Common/QueryUtils/Models";
import { getQueryUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import { QueryFavoriteButton } from "Wit/QueriesHub/Common/Components/QueryFavoriteButton";
import { QueryMoreButton } from "Wit/QueriesHub/Common/Components/QueryMoreButton";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";

const c_lastVisitedQueryGroupKey = "LAST_VISITED_QUERY_GROUP";

interface IFavoriteQueriesTabProps {
    filteredFavoriteGroups: ObservableArray<ObservableFavoriteItemsGroup>;
    isFiltered: ObservableValue<boolean>;
    isLoading: ObservableValue<boolean>;
    onUpdateFilter: () => void;
}

interface QueryGroupItem {
    id: string;
    children: ExtendedHierarchyItem[];
    depth: 0;
    isEmptyFolderContext: false;
    isFolder: true;
    isLoadingContext: false;
    name: string;
}

export class FavoriteQueriesTab extends VssComponent<IFavoriteQueriesTabProps> {
    private _contextMenuBehavior: ContextMenuBehavior;
    private _listService: IQueryListService;
    private _moreButtonRefs: React.RefObject<QueryMoreButton>[] = [];
    private _treeItemProvider: TreeItemProvider<QueryGroupItem | QueryItem>;
    private _shortcutService: IVssKeyboardShortcutService;

    constructor(props: IFavoriteQueriesTabProps, context: IVssComponentContext) {
        super(props, context);
        this._listService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        this._getFavoriteQueries();
        this._contextMenuBehavior = new ContextMenuBehavior(this._onContextMenu, true);
        this._shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this._listService.queryTree.length === 0) {
            this._listService.getQueriesAsync();
        }
        this.registerShortcuts();
    }

    private registerShortcuts = (): void => {
        this._shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["z"],
            description: Resources.ToggleFullScreenMode,
            action: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.toggleFullScreenMode();
            }
        });
    }

    public componentWillUnmount() {
        this.unregisterShortcuts();
    }

    private unregisterShortcuts = () => {
        this._shortcutService.removeShortcutGroup(Resources.QueriesHubHeader);
    };

    public render(): JSX.Element {
        return (
            <Card className="flex-grow bolt-table-card bolt-queries-card" contentProps={{ contentPadding: false }}>
                <Observer
                    favoriteGroups={
                        {
                            observableValue: this._listService.favoriteGroups,
                            filter: () => {
                                this._getFavoriteQueries();
                                this.props.onUpdateFilter();
                                return true;
                            }
                        } as IObservableExpression<ObservableFavoriteItemsGroup[]>
                    }
                    filteredFavoriteGroups={
                        {
                            observableValue: this.props.filteredFavoriteGroups,
                            filter: () => {
                                this._getFavoriteQueries();
                                return true;
                            }
                        } as IObservableExpression<ObservableFavoriteItemsGroup[]>
                    }
                    isFiltered={this.props.isFiltered}
                    isLoading={this.props.isLoading}
                >
                    {(observerableProps: {
                        favoriteGroups: ObservableFavoriteItemsGroup[];
                        filteredFavoriteGroups: ObservableFavoriteItemsGroup[];
                        isFiltered: boolean;
                        isLoading: boolean;
                    }) => {
                        return observerableProps.isLoading ? (
                            <Spinner
                                className="flex flex-center queries-loading-spinner queries-minimum-card-height"
                                size={SpinnerSize.large}
                                label={Resources.Loading}
                            />
                        ) : observerableProps.favoriteGroups.length === 0 ||
                            (observerableProps.isFiltered && observerableProps.filteredFavoriteGroups.length === 0) ? (
                            <ZeroData
                                className={"queries-hub-zero-data"}
                                primaryText={Resources.NoFavoriteQueriesText}
                                imagePath={getAssetLocation(
                                    this.context.pageContext,
                                    "ms.vss-work-web/new-queries-hub-content/Illustrations/general-no-results-found.svg"
                                )}
                                imageAltText={Resources.NoFavoriteQueriesText}
                            ></ZeroData>
                        ) : (
                            <Tree
                                ariaLabel={Resources.FavoritesTabName}
                                behaviors={[this._contextMenuBehavior]}
                                columns={[this.titleColumn, this.favoriteColumn, this.moreColumn, this.folderColumn, this.lastModifiedColumn]}
                                itemProvider={this._treeItemProvider}
                                showLines={true}
                                singleClickActivation={false}
                                onToggle={(event, treeItem: ITreeItemEx<QueryGroupItem | QueryItem>) => {
                                    this._treeItemProvider.toggle(treeItem.underlyingItem);
                                }}
                                scrollable={false}
                            />
                        );
                    }}
                </Observer>
            </Card>
        );
    }

    private _getFavoriteQueries() {
        try {
            const favorites = this.props.isFiltered.value ? this.props.filteredFavoriteGroups.value : this._listService.favoriteGroups.value;
            if (favorites && favorites.length > 0) {
                this._treeItemProvider = new TreeItemProvider(this._convertGroupsToTreeItems(favorites));
            }
        } catch (error) {
            // do nothing because we already reported it in IQueryListService
        }

        this.props.isLoading.value = false;
    }
    private _convertGroupsToTreeItems(favorites: ObservableFavoriteItemsGroup[]): ITreeItem<QueryGroupItem | QueryItem>[] {
        let treeItems: ITreeItem<QueryGroupItem | QueryItem>[] = [];
        favorites.forEach((group: ObservableFavoriteItemsGroup) => {
            treeItems.push({
                childItems: this._convertItemsToTreeItems(group.items),
                data: {
                    children: group.items,
                    id: group.id,
                    isFolder: true,
                    depth: 0,
                    name: group.name,
                    isEmptyFolderContext: false,
                    isLoadingContext: false
                } as QueryGroupItem,
                expanded: true
            });
        });
        return treeItems;
    }

    private _convertItemsToTreeItems(favorites: QueryItem[]): ITreeItem<QueryItem>[] {
        let treeItems: ITreeItem<QueryItem>[] = [];
        favorites.forEach(query => {
            treeItems.push({
                data: { ...query }
            });
        });
        return treeItems;
    }

    private _onSize = (event: MouseEvent, index: number, width: number, column: ITreeColumn<QueryItem>) => {
        (column.width as ObservableValue<number>).value = width;
    };

    private _onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        const rowIndex = cellFromEvent(event).rowIndex;
        if (this._moreButtonRefs[rowIndex]?.current) {
            this._moreButtonRefs[rowIndex]!.current!.expand();
            event.preventDefault();
        }
    };

    private _renderTreeCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<QueryItem>,
        treeItem: ITreeItemEx<QueryItem>
    ): JSX.Element => {
        if (!this._moreButtonRefs[rowIndex]) {
            this._moreButtonRefs[rowIndex] = React.createRef<QueryMoreButton>();
        }
        const item = treeItem.underlyingItem.data;
        let content: JSX.Element;
        let text = item.isFolder && item.id !== c_lastVisitedQueryGroupKey ? `${item.name} ${Resources.FavoritesLabel}` : item.name;
        const queryTypeLabel = QueryUtils.getQueryTypeShortText(item.queryType, item.isInvalidSyntax);
        const iconName = QueryUtils.getQueryTypeIcon(item.queryType);
        const icon = !item.isFolder ? (
            <Tooltip text={queryTypeLabel}>
                <Icon className={"margin-right-8"} iconName={iconName} />
            </Tooltip>
        ) : undefined;
        content = (
            <div className="flex-row flex-grow text-ellipsis">
                <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                    {icon}
                    <Tooltip overflowOnly={true}>
                        {item.isFolder ? (
                            <span className="text-ellipsis">{text}</span>
                        ) : (
                                <Link
                                    className="text-ellipsis"
                                    href={getQueryUrl(this.context.pageContext, item.id)}
                                    subtle={true}
                                    removeUnderline={true}
                                >
                                    {text}
                                </Link>
                        )}
                    </Tooltip>
                </span>
            </div>
        );

        return ExpandableTreeCell({
            children: content,
            columnIndex,
            contentClassName: undefined,
            treeColumn,
            treeItem
        });
    };

    private _renderFavoriteCell = (rowIndex: number, columnindex: number, treeColumn: ITreeColumn<QueryItem>, treeItem: ITreeItemEx<QueryItem>) => {
        const item = treeItem.underlyingItem.data;
        const content = (
            <SimpleTableCell columnIndex={columnindex} key={"favorite-cell" + rowIndex}>
                <QueryFavoriteButton
                    className={"queries-hub-favorite-cell"}
                    item={item}
                    onAddFavorite={() => this._listService.addFavorite(item)}
                    onRemoveFavorite={() => this._listService.removeFavorite(item)}
                />
            </SimpleTableCell>
        );

        return item.isFolder ? <SimpleTableCell columnIndex={columnindex} key={"empty-favorite" + rowIndex} /> : content;
    };

    private _renderMoreCell = (rowIndex: number, columnindex: number, treeColumn: ITreeColumn<QueryItem>, treeItem: ITreeItemEx<QueryItem>) => {
        const item = treeItem.underlyingItem.data;
        return !item.isFolder ? (
            <SimpleTableCell contentClassName="bolt-list-cell-content-reveal" columnIndex={columnindex} key={"more-cell" + item.id}>
                <QueryMoreButton item={item as QueryItem} ref={this._moreButtonRefs[rowIndex]} />
            </SimpleTableCell>
        ) : (
            <SimpleTableCell columnIndex={columnindex} key={"empty-column-more" + rowIndex} />
        );
    };

    private _renderFolderCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<QueryItem>,
        treeItem: ITreeItemEx<QueryItem>
    ): JSX.Element => {
        if (treeItem.underlyingItem.data.isFolder) {
            return <SimpleTableCell ariaRowIndex={rowIndex + 1} columnIndex={columnIndex} key={"col-" + columnIndex + " row-" + rowIndex} />;
        }

        let folderName = treeItem.underlyingItem.data.path;
        const parts = folderName.split("/");
        folderName = parts ? parts.slice(0, parts.length - 1).join("/") : folderName;
        return (
            <SimpleTableCell ariaRowIndex={rowIndex + 1} columnIndex={columnIndex} key={`col-${columnIndex} row-${rowIndex}`}>
                <Tooltip overflowOnly={true} text={folderName}>
                    <span className="text-ellipsis">{folderName}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private _renderModifiedCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<QueryItem>,
        treeItem: ITreeItemEx<QueryItem>
    ): JSX.Element => {
        if (treeItem.underlyingItem.data.isFolder) {
            return <SimpleTableCell ariaRowIndex={rowIndex + 1} columnIndex={columnIndex} key={"col-" + columnIndex + " row-" + rowIndex} />;
        }
        const identity = treeItem.underlyingItem.data.lastModifiedBy;
        const imageUrl = getIdentityImageUrl(this.context.pageContext, identity);
        const dayInSeconds = 60 * 60 * 24;
        const text = Utils.format(
            Resources.Updated,
            identity.displayName,
            QueryUtils.getFriendlyDate(treeItem.underlyingItem.data.lastModifiedDate, dayInSeconds)
        );
        return (
            <SimpleTableCell
                ariaRowIndex={rowIndex + 1}
                columnIndex={columnIndex}
                contentClassName="text-ellipsis"
                key={`col-${columnIndex} row-${rowIndex}`}
            >
                <VssPersona cssClass={"margin-right-8"} displayName={identity.displayName} imageUrl={imageUrl} size="extra-small"></VssPersona>
                <Tooltip overflowOnly={false} text={text}>
                    <span>{text}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private titleColumn: ITreeColumn<QueryItem> = {
        id: "title",
        name: Resources.TitleColumn,
        width: new ObservableValue(-100),
        renderCell: this._renderTreeCell,
        onSize: this._onSize
    };
    private favoriteColumn: ITreeColumn<QueryItem> = {
        ariaLabel: Resources.Favorite,
        id: "favorite-column",
        readonly: true,
        renderCell: this._renderFavoriteCell,
        width: 32 + QueryUtils.getColumnHeaderPadding()
    };
    private moreColumn: ITreeColumn<QueryItem> = {
        ariaLabel: Resources.ContextMenuColumn,
        id: "more-button-column",
        readonly: true,
        renderCell: this._renderMoreCell,
        width: 32 + QueryUtils.getColumnHeaderPadding()
    };
    private folderColumn: ITreeColumn<QueryItem> = {
        id: "folder",
        name: Resources.FolderColumn,
        width: new ObservableValue(-50),
        renderCell: this._renderFolderCell,
        onSize: this._onSize
    };
    private lastModifiedColumn: ITreeColumn<QueryItem> = {
        id: "last-modified",
        name: Resources.LastModifiedColumn,
        width: new ObservableValue(-50),
        renderCell: this._renderModifiedCell,
        onSize: this._onSize
    };
}
